import { useEffect, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { Ruler, Smartphone, Target } from 'lucide-react';
import { PageHeader } from '@/components/PageHeader';
import { Card, Dato, Seccion } from '@/components/Card';
import { Banner } from '@/components/Banner';
import { SelectField } from '@/components/Field';
import { Fila, Filas } from '@/components/Fila';
import { Segmentado } from '@/components/Segmentado';
import { DIAS_ALERTA } from '@/domain/backup-reminder';
import { actualizarSettings, contarRegistros, leerSettings } from '@/db/schema';
import { ETIQUETAS_TABLA, ETIQUETAS_TEMA, TABLAS, type Tema } from '@/db/types';
import { mensajeDeError } from '@/db/tx';
import { estadoAlmacenamiento, formatearBytes, type EstadoAlmacenamiento } from '@/lib/storage';
import { establecerTema } from '@/lib/tema';
import { detectarPlataforma } from '@/features/pwa/platform';
import { BuscarActualizacion } from './BuscarActualizacion';
import { AvisosDeDescanso } from './AvisosDeDescanso';
import { MetodoDeAyuno } from '@/features/food/AyunoPages';

const TEMAS: readonly Tema[] = ['sistema', 'claro', 'oscuro'];

const DESCRIPCION_TEMA: Readonly<Record<Tema, string>> = {
  sistema: 'Sistema sigue la apariencia de tu teléfono.',
  claro: 'Siempre claro, aunque el iPhone esté en modo oscuro.',
  oscuro: 'Siempre oscuro, aunque el iPhone esté en modo claro.',
};

/** Días para el aviso de respaldo (Req. 7.4). A los 14 siempre es alerta (Req. 7.1). */
const DIAS_AVISO: readonly number[] = [1, 3, 5, 7, 10, DIAS_ALERTA];

/** Más → Ajustes (Req. 1.6, 2.5, 2.9, 7.4, 8.3), con el estilo de Foco. */
export function SettingsPage() {
  const settings = useLiveQuery(() => leerSettings(), []);
  const conteos = useLiveQuery(() => contarRegistros(), []);
  const [almacenamiento, setAlmacenamiento] = useState<EstadoAlmacenamiento | null>(null);
  const [plataforma] = useState(() => detectarPlataforma());
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let vigente = true;
    void estadoAlmacenamiento().then((estado) => {
      if (vigente) setAlmacenamiento(estado);
    });
    return () => {
      vigente = false;
    };
  }, []);

  async function cambiarTema(tema: Tema) {
    setError(null);
    establecerTema(tema);
    try {
      await actualizarSettings({ tema });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  async function cambiarDiasAviso(texto: string) {
    const dias = Number(texto);
    if (!Number.isInteger(dias) || dias < 1) return;
    setError(null);
    try {
      await actualizarSettings({ diasAvisoRespaldo: dias });
    } catch (e) {
      setError(mensajeDeError(e));
    }
  }

  const diasAviso = settings?.diasAvisoRespaldo ?? 7;

  const persistencia =
    almacenamiento?.persistente === true
      ? 'Concedido'
      : almacenamiento?.persistente === false
        ? 'No concedido'
        : 'No disponible';

  return (
    <div className="space-y-6">
      <PageHeader
        titulo="A tu manera."
        descripcion="Pequeños ajustes para sentirla tuya."
        volver={{ a: '/mas', texto: 'Volver', etiqueta: 'Volver a Más' }}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <Seccion titulo="Apariencia">
        <Segmentado
          etiqueta="Tema"
          completo
          valor={settings?.tema ?? 'sistema'}
          opciones={TEMAS.map((t) => ({
            valor: t,
            etiqueta: t === 'sistema' ? 'Sistema' : ETIQUETAS_TEMA[t],
          }))}
          onCambio={(t) => void cambiarTema(t)}
        />
        <p className="text-[12px] text-texto-suave">
          {DESCRIPCION_TEMA[settings?.tema ?? 'sistema']} También puedes cambiarlo con el sol y la
          luna de arriba.
        </p>
      </Seccion>

      <AvisosDeDescanso
        activo={settings?.avisoConPantallaBloqueada ?? false}
        conAppCerrada={settings?.avisoConAppCerrada ?? false}
      />

      <MetodoDeAyuno metodo={settings?.metodoAyuno ?? 'comidas'} />

      <section>
        <SelectField
          etiqueta="Recordatorio de respaldo"
          valor={String(diasAviso)}
          // Incluye el valor guardado aunque no sea de la lista, por ejemplo al restaurar
          opciones={[...new Set([...DIAS_AVISO, diasAviso])]
            .sort((a, b) => a - b)
            .map((d) => ({
              valor: String(d),
              etiqueta: d === 1 ? 'Después de 1 día' : `Después de ${d} días`,
            }))}
          onCambio={(v) => void cambiarDiasAviso(v)}
          ayuda={`Safari no permite respaldar solo, así que la app te lo recuerda en todas las pantallas. A los ${DIAS_ALERTA} días, o si nunca has respaldado y ya hay datos, el aviso se vuelve alerta.`}
        />
      </section>

      <Filas>
        <Fila
          icono={Ruler}
          titulo="Unidades y datos personales"
          detalle="Tu nombre y las unidades de peso y medidas"
          a="/mas/perfil"
        />
        <Fila
          icono={Target}
          titulo="Metas sin plan"
          detalle="kcal, macros y ayuno para los días sin plan"
          a="/mas/metas"
        />
        {!plataforma.enStandalone && (
          <Fila
            icono={Smartphone}
            titulo="Cómo instalar la app"
            detalle="Estás usando el navegador"
            a="/mas/instalar"
          />
        )}
      </Filas>

      <BuscarActualizacion />

      <Seccion titulo="Almacenamiento de este teléfono">
        <Card>
          <dl className="divide-y divide-borde">
            <Dato
              etiqueta="Espacio usado"
              valor={formatearBytes(almacenamiento?.usadoBytes ?? null)}
            />
            <Dato
              etiqueta="Espacio disponible"
              valor={formatearBytes(almacenamiento?.cuotaBytes ?? null)}
            />
            <Dato
              etiqueta="Almacenamiento persistente"
              valor={persistencia}
              detalle="Pide al navegador no borrar los datos por falta de espacio"
            />
            <Dato
              etiqueta="Modo de ejecución"
              valor={plataforma.enStandalone ? 'App instalada' : 'Navegador'}
            />
          </dl>
        </Card>

        {almacenamiento?.persistente === false && (
          <Banner tono="aviso" titulo="Safari no concedió almacenamiento persistente">
            En iPhone esto es normal y no significa que algo esté mal. La protección real es usar
            la app instalada y hacer respaldos con frecuencia.
          </Banner>
        )}
      </Seccion>

      <Seccion
        titulo="Registros guardados"
        descripcion="Sirve para comprobar que una restauración trajo todo."
      >
        <ul className="divide-y divide-borde">
          {TABLAS.filter((t) => t !== 'settings').map((tabla) => (
            <li key={tabla} className="flex justify-between gap-4 py-2.5 text-[13px]">
              <span className="text-texto-suave">{ETIQUETAS_TABLA[tabla]}</span>
              <span className="font-medium tabular-nums text-texto">{conteos?.[tabla] ?? 0}</span>
            </li>
          ))}
        </ul>
      </Seccion>

      <Banner tono="alerta" titulo="Tus datos están solo en este teléfono">
        No hay copia en la nube. Borrar el ícono o los datos de navegación del iPhone elimina toda
        la información de la app, y no hay forma de recuperarla sin un respaldo exportado.
      </Banner>
    </div>
  );
}
